'use client';
import React, { createContext, useContext, useDebugValue, useState, useEffect, useCallback, useMemo } from 'react';
import Script from 'next/script';

// TODO: Improve types
/**
 * Locales supported by HubSpot forms
 */
var HubSpotFormLocale;
(function (HubSpotFormLocale) {
    HubSpotFormLocale["ENGLISH"] = "en";
    HubSpotFormLocale["DANISH"] = "da";
    HubSpotFormLocale["GERMAN"] = "de";
    HubSpotFormLocale["SPANISH"] = "es";
    HubSpotFormLocale["SPANISH_MEXICO"] = "es-mx";
    HubSpotFormLocale["FINNISH"] = "fi";
    HubSpotFormLocale["FRENCH"] = "fr";
    HubSpotFormLocale["ITALIAN"] = "it";
    HubSpotFormLocale["JAPANESE"] = "ja";
    HubSpotFormLocale["DUTCH"] = "nl";
    HubSpotFormLocale["POLISH"] = "pl";
    HubSpotFormLocale["PORTUGUESE"] = "pt-br";
    HubSpotFormLocale["SWEDISH"] = "sv";
    HubSpotFormLocale["CHINESE"] = "zh-cn";
    HubSpotFormLocale["CHINESE_HONG_KONG"] = "zh-hk";
})(HubSpotFormLocale || (HubSpotFormLocale = {}));

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const HubspotContext = createContext({
    loaded: false,
    error: false,
});
const useHubspotContext = () => {
    const values = useContext(HubspotContext);
    useDebugValue(`Hubspot Script: ${(values === null || values === void 0 ? void 0 : values.loaded) ? "Loaded" : "Not Loaded"}`);
    useDebugValue(`Failed to load Script: ${(values === null || values === void 0 ? void 0 : values.error) ? "Yes" : "No"}`);
    return values;
};
/** Loads Hubspot script to the document and syncs loading state between forms on the page */
const HubspotProvider = (_a) => {
    var { children, id = "hubspotScript", src = "https://js.hsforms.net/forms/v2.js", strategy = "afterInteractive", onLoad: passedOnLoad, onError: passedOnError } = _a, props = __rest(_a, ["children", "id", "src", "strategy", "onLoad", "onError"]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    // Reset state when script src is changed
    useEffect(() => {
        setLoaded(false);
        setError(false);
    }, [src]);
    // Handle script load
    const onLoad = useCallback((e) => {
        setLoaded(true);
        passedOnLoad === null || passedOnLoad === void 0 ? void 0 : passedOnLoad(e);
    }, [passedOnLoad]);
    // Handle script error
    const onError = useCallback((e) => {
        setError(true);
        passedOnError === null || passedOnError === void 0 ? void 0 : passedOnError(e);
    }, [passedOnError]);
    // Prevent unnecessary rerenders
    const value = useMemo(() => ({ loaded, error }), [loaded, error]);
    return (React.createElement(HubspotContext.Provider, { value: value },
        children,
        React.createElement(Script, Object.assign({ id: id, src: src, strategy: strategy, onLoad: onLoad, onError: onError }, props))));
};

const useHubspotForm = (formProps) => {
    const { loaded, error } = useHubspotContext();
    const [formCreated, setFormCreated] = useState(false);
    useEffect(() => {
        if (loaded && !formCreated) {
            try {
                window.hbspt.forms.create(formProps);
                setFormCreated(true);
            }
            catch (e) {
                console.warn("Failed to create hubspot form:");
                console.error(e);
                setFormCreated(false);
            }
        }
        // Don't include 'formProps' here to avoid rerenders
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded, formCreated, setFormCreated]);
    useDebugValue(`Form created: ${formCreated}`);
    return { loaded, formCreated, error };
};

export { HubSpotFormLocale, HubspotContext, HubspotProvider, useHubspotContext, useHubspotForm };
//# sourceMappingURL=index.esm.js.map
